import { useCallback, useEffect, useMemo, useState } from 'react';

export type ColumnPreference = {
  key: string;
  visible: boolean;
  width?: number;
};

type ColumnWithSettings = {
  dataSourceKey?: string;
  customizationKey?: string;
  hidden?: boolean;
  responsive?: unknown;
  mandatory?: boolean;
  pinned?: 'left' | 'right';
  expandableOnly?: boolean;
  width?: number | string;
};

type UseTableColumnSettingsParams<T extends ColumnWithSettings> = {
  tableId: string;
  columns: T[];
  enabled?: boolean;
};

const STORAGE_PREFIX = 'vl-table-column-settings';

const getStorageKey = (tableId: string) => `${STORAGE_PREFIX}:${tableId}`;

const getColumnKey = (column: ColumnWithSettings) => column.customizationKey ?? column.dataSourceKey;

const isColumnPreference = (value: unknown): value is ColumnPreference => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const preference = value as ColumnPreference;

  return (
    typeof preference.key === 'string' &&
    typeof preference.visible === 'boolean' &&
    (preference.width === undefined || (typeof preference.width === 'number' && Number.isFinite(preference.width)))
  );
};

const readPreferences = (storageKey: string): ColumnPreference[] | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storedValue = window.localStorage.getItem(storageKey);

  if (!storedValue) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(storedValue);

    if (!Array.isArray(parsed) || !parsed.every(isColumnPreference)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

const useTableColumnSettings = <T extends ColumnWithSettings>({
  tableId,
  columns,
  enabled = true
}: UseTableColumnSettingsParams<T>) => {
  const storageKey = useMemo(() => getStorageKey(tableId), [tableId]);

  const [savedPreferences, setSavedPreferences] = useState<ColumnPreference[] | null>(() =>
    enabled ? readPreferences(storageKey) : null
  );

  useEffect(() => {
    setSavedPreferences(enabled ? readPreferences(storageKey) : null);
  }, [enabled, storageKey]);

  const defaultPreferences = useMemo<ColumnPreference[]>(
    () =>
      columns.flatMap((column) => {
        const key = getColumnKey(column);

        if (!key || column.expandableOnly) return [];

        return [
          {
            key,
            visible: column.mandatory || !column.hidden
          }
        ];
      }),
    [columns]
  );

  const preferences = useMemo(() => {
    if (!savedPreferences) {
      return defaultPreferences;
    }

    const availableKeys = new Set(defaultPreferences.map(({ key }) => key));

    const existingPreferences = savedPreferences.filter(({ key }) => availableKeys.has(key));

    const savedKeys = new Set(existingPreferences.map(({ key }) => key));

    const newColumns = defaultPreferences.filter(({ key }) => !savedKeys.has(key));

    return [...existingPreferences, ...newColumns];
  }, [defaultPreferences, savedPreferences]);

  const hasColumnLayoutCustomization = useMemo(() => {
    if (!savedPreferences) {
      return false;
    }

    const savedWithoutWidth = savedPreferences.map(({ key, visible }) => ({
      key,
      visible
    }));

    return JSON.stringify(savedWithoutWidth) !== JSON.stringify(defaultPreferences);
  }, [savedPreferences, defaultPreferences]);

  const customizedColumns = useMemo(() => {
    if (!enabled || !savedPreferences) {
      return columns;
    }

    const columnsByKey = new Map(
      columns.flatMap((column) => {
        const key = getColumnKey(column);

        return key ? [[key, column] as const] : [];
      })
    );

    const usedKeys = new Set<string>();

    const orderedColumns = preferences.flatMap((preference) => {
      const column = columnsByKey.get(preference.key);

      if (!column) {
        return [];
      }

      usedKeys.add(preference.key);

      const defaultPreference = defaultPreferences.find(({ key }) => key === preference.key);
      const isExplicitlyHidden =
        !column.mandatory && defaultPreference?.visible === true && preference.visible === false;

      return [
        {
          ...column,
          hidden: !column.mandatory && !isExplicitlyHidden && !preference.visible,
          expandableOnly: column.expandableOnly || isExplicitlyHidden,
          responsive: hasColumnLayoutCustomization && preference.visible ? undefined : column.responsive,
          width: preference.width ?? column.width
        }
      ];
    });

    const columnsWithoutPreference = columns
      .filter((column) => {
        const key = getColumnKey(column);

        return !key || !usedKeys.has(key);
      })
      .map((column) =>
        column.expandableOnly
          ? {
              ...column,
              hidden: true
            }
          : column
      );

    return [...orderedColumns, ...columnsWithoutPreference];
  }, [columns, enabled, preferences, savedPreferences, hasColumnLayoutCustomization]);

  const save = useCallback(
    (nextPreferences: ColumnPreference[]) => {
      if (!enabled) {
        return;
      }

      const hasVisibilityChanged = nextPreferences.some((preference) => {
        const previous = preferences.find(({ key }) => key === preference.key);

        return previous?.visible !== preference.visible;
      });

      const normalizedPreferences = nextPreferences.map((preference) => {
        if (!hasVisibilityChanged) {
          return preference;
        }

        // oxlint-disable-next-line no-unused-vars
        const { width: _, ...withoutWidth } = preference;

        return withoutWidth;
      });

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, JSON.stringify(normalizedPreferences));
      }

      setSavedPreferences(normalizedPreferences);
    },
    [enabled, preferences, storageKey]
  );

  const saveWidths = useCallback(
    (widths: Record<string, number>) => {
      save(
        preferences.map((preference) => {
          const width = widths[preference.key];

          return width !== undefined
            ? {
                ...preference,
                width
              }
            : preference;
        })
      );
    },
    [preferences, save]
  );

  const reset = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(storageKey);
    }

    setSavedPreferences(null);
  }, [storageKey]);

  return {
    columns: customizedColumns,
    preferences,
    hasCustomPreferences: savedPreferences !== null,
    hasColumnLayoutCustomization,
    save,
    saveWidths,
    reset
  };
};

export default useTableColumnSettings;
